import get from "lodash/get";
import { InterestQuestionsSelector } from "PFApp/components/activity_form/form_selectors/interest_questions_selector";
import {
  FIELD_INTEREST_QUESTIONS_KEY,
  FIELD_INTEREST_QUESTIONS_TOGGLE_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useInterestQuestionariesTemplateConfiguration } from "PFApp/hooks";
import Toggle from "PFComponents/toggle/toggle";
import { useCallback } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useActivityFormStore } from "../../activity_form_store";
import { FormSection, SectionProps } from "../form_section";

export const ShowInterestQuestionsFormSection = ({ id, title }: SectionProps) => {
  const { isNew } = useActivityFormStore((state) => state.activityStatus);
  const template = useActivityFormStore((state) => state.template);
  const { t } = useTranslation("activities", { keyPrefix: "form.sections.showInterestQuestions" });

  const templateConfig = useInterestQuestionariesTemplateConfiguration(template);

  const isToggleVisible = isNew && !!templateConfig?.allowRmToEnable;
  const areQuestionsEditable = isNew && !!templateConfig?.allowRmToEdit;

  const {
    clearErrors,
    control,
    setValue,
    formState: { errors }
  } = useFormContext();

  const isToggleSwitched = useWatch({ name: FIELD_INTEREST_QUESTIONS_TOGGLE_KEY });

  const handleQuestionsToggleChange = useCallback(
    (newValue: boolean) => {
      clearErrors(FIELD_INTEREST_QUESTIONS_KEY);
      setValue(FIELD_INTEREST_QUESTIONS_TOGGLE_KEY, newValue, { shouldDirty: true });
    },
    [clearErrors, setValue]
  );

  const handleQuestionsChange = useCallback(
    (newQuestions: string[]) => {
      setValue(FIELD_INTEREST_QUESTIONS_KEY, newQuestions, { shouldDirty: true });
    },
    [setValue]
  );

  return (
    <FormSection
      id={id}
      title={title}
      actions={
        isToggleVisible ? (
          <Controller
            name={FIELD_INTEREST_QUESTIONS_TOGGLE_KEY}
            control={control}
            render={({ field }) => <Toggle onChange={handleQuestionsToggleChange} checked={field.value} />}
          />
        ) : null
      }
    >
      {isToggleSwitched && (
        <Controller
          name={FIELD_INTEREST_QUESTIONS_KEY}
          control={control}
          rules={{
            validate: (value: string[]) => {
              if (isToggleSwitched && value.every((question) => !question.trim())) {
                return t("interestQuestionsError");
              } else {
                return true;
              }
            }
          }}
          render={({ field }) => (
            <InterestQuestionsSelector
              disabled={!areQuestionsEditable}
              questions={field.value}
              error={get(errors, field.name)?.message as string}
              onQuestionsChange={handleQuestionsChange}
            />
          )}
        />
      )}
    </FormSection>
  );
};
